import {
  activateSigningRequest,
  addDocuments,
  addParticipant,
  createField,
  createSigningRequest,
  deleteDocument,
  deleteField,
  deleteParticipant,
  getDocuments,
  getFields,
  getParticipants,
  updateField,
  updateSigningRequest,
  SignatureFieldType,
  type CreateParticipants,
  type CreateFieldData,
  type ParticipantInDb,
  type SignatureFieldInDb,
  type SigningRequestCreate,
  type SigningRequestInDb,
  type VaultFileRevisionSigningRequestCreate,
} from '@/api';
import type {
  SignatureActivateRequest,
  SignatureActivateResponse,
  SignatureDraftField,
} from '@/types/contractManagement/signatureFlow';
import { extractErrorDetail } from '@/utils/errorHandler';

type SignatureFieldCreate = CreateFieldData['body'];

const DEFAULT_TEXT_MAX_LENGTH = 500;
const DEFAULT_TEXT_FIELD_QUESTION = 'Text';
const DEFAULT_MENTION_TEXT = 'Mention';
const DEFAULT_READ_ONLY_TEXT = 'Text';

async function initializeYoutrustSignature(
  payload: SigningRequestCreate
): Promise<SigningRequestInDb> {
  const response = await createSigningRequest({
    body: payload,
  });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, 'Failed to initialize the signature request.')
    );
  }

  return response.data;
}

async function uploadYoutrustDocument(
  signingRequestUid: string,
  payload: VaultFileRevisionSigningRequestCreate[]
): Promise<SigningRequestInDb> {
  const response = await addDocuments({
    path: { signing_request_uid: signingRequestUid },
    body: payload,
  });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, 'Failed to upload the document to the signature request.')
    );
  }

  return response.data;
}

async function addYoutrustSignees(
  signingRequestUid: string,
  payload: CreateParticipants
): Promise<SigningRequestInDb> {
  const response = await addParticipant({
    path: { signing_request_uid: signingRequestUid },
    body: payload,
  });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, 'Failed to add signees to the signature request.')
    );
  }

  return response.data;
}

async function activateYoutrustSignature(signingRequestUid: string): Promise<SigningRequestInDb> {
  const response = await activateSigningRequest({
    path: { signing_request_uid: signingRequestUid },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to send the signature request.'));
  }

  return response.data;
}

async function fetchPersistedParticipants(signingRequestUid: string): Promise<ParticipantInDb[]> {
  const response = await getParticipants({
    path: { signing_request_uid: signingRequestUid },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to load signing participants.'));
  }

  return response.data;
}

function buildParticipantUidByEntityUid(participants: ParticipantInDb[]): Map<string, string> {
  const mapping = new Map<string, string>();

  // Draft fields address recipients by contractual party uid.
  for (const participant of participants) {
    const partyUid = participant.contractual_party?.uid;
    if (partyUid) {
      mapping.set(partyUid, participant.uid);
    }
  }

  return mapping;
}

function nonEmptyFieldText(value: string | undefined | null, fallback: string): string {
  const trimmedValue = value?.trim();
  return trimmedValue && trimmedValue.length > 0 ? trimmedValue : fallback;
}

function textFieldStyle(field: SignatureDraftField): {
  font_family: string | undefined;
  font_color: string | undefined;
  font_size: number | undefined;
  font_bold: boolean | undefined;
  font_italic: boolean | undefined;
} {
  return {
    font_family: field.font_family,
    font_color: field.font_color,
    font_size: field.font_size,
    font_bold: field.font_bold,
    font_italic: field.font_italic,
  };
}

function buildSignatureFieldCreatePayload(field: SignatureDraftField): SignatureFieldCreate {
  const common = {
    page: field.page,
    vault_file_revision_uid: field.vault_file_revision_uid,
    x: field.x,
    y: field.y,
  };

  switch (field.type) {
    case SignatureFieldType.SIGNATURE:
      return {
        ...common,
        type: 'signature',
        width: field.width,
        height: field.height,
        reason: field.reason ?? null,
      };
    case SignatureFieldType.CHECKBOX:
      return {
        ...common,
        type: 'checkbox',
        size: field.size,
        optional: field.optional ?? false,
        name: field.name ?? null,
      };
    case SignatureFieldType.MENTION:
      return {
        ...common,
        ...textFieldStyle(field),
        type: 'mention',
        width: field.width ?? 20,
        height: field.height ?? 8,
        mention: nonEmptyFieldText(field.mention, DEFAULT_MENTION_TEXT),
        name: field.name ?? null,
      };
    case SignatureFieldType.TEXT:
      return {
        ...common,
        ...textFieldStyle(field),
        type: 'text',
        width: field.width ?? 20,
        height: field.height ?? 8,
        question: nonEmptyFieldText(field.question, DEFAULT_TEXT_FIELD_QUESTION),
        max_length: field.max_length ?? DEFAULT_TEXT_MAX_LENGTH,
        instruction: field.instruction ?? null,
        optional: field.optional ?? false,
        name: field.name ?? null,
        default_value: field.default_value ?? null,
        read_only: field.read_only ?? false,
      };
    case SignatureFieldType.SIGNATURE_DATE:
      return {
        ...common,
        ...textFieldStyle(field),
        type: 'signature_date',
        name: field.name ?? null,
      };
    case SignatureFieldType.SIGNER_NAME:
      return {
        ...common,
        ...textFieldStyle(field),
        type: 'signer_name',
        name_format: field.name_format ?? 'full_name',
        name: field.name ?? null,
      };
    case SignatureFieldType.SIGNER_EMAIL:
      return {
        ...common,
        ...textFieldStyle(field),
        type: 'signer_email',
        name: field.name ?? null,
      };
    case SignatureFieldType.READ_ONLY_TEXT:
      return {
        ...common,
        ...textFieldStyle(field),
        type: 'read_only_text',
        width: field.width ?? 20,
        height: field.height ?? 8,
        text: nonEmptyFieldText(field.text, DEFAULT_READ_ONLY_TEXT),
      };
  }
}

async function createPersistedSignatureField(
  participantUid: string,
  signingRequestUid: string,
  field: SignatureFieldCreate
): Promise<string> {
  const response = await createField({
    path: {
      participant_uid: participantUid,
      signing_request_uid: signingRequestUid,
    },
    body: field,
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to create signature field.'));
  }

  return response.data.uid;
}

/**
 * Identity of a participant for diffing: a changed role, signature type or
 * signing representative all need a delete + re-add.
 */
function participantSignature(
  role: string,
  signatureType: string | null | undefined,
  representativeUid: string | null | undefined
): string {
  return `${role}|${signatureType ?? ''}|${representativeUid ?? ''}`;
}

/** Every signature field currently attached to the request, across all its participants. */
async function fetchPersistedSignatureFields(
  signingRequestUid: string,
  participants: ParticipantInDb[]
): Promise<SignatureFieldInDb[]> {
  const perParticipant = await Promise.all(
    participants.map(async participant => {
      const response = await getFields({
        path: { signing_request_uid: signingRequestUid, participant_uid: participant.uid },
      });

      if (response.error) {
        throw new Error(extractErrorDetail(response.error, 'Failed to load signature fields.'));
      }

      return response.data;
    })
  );

  return perParticipant.flat();
}

/**
 * The per-type properties a `SignatureFieldUpdate` cannot carry. Hydration leaves every one of
 * them unset (`SignatureFieldInDb` doesn't expose them), so finding one set on a loaded field
 * means the user edited it in the sidebar — which only an update-by-recreate can persist.
 */
const LOCAL_ONLY_FIELD_DETAIL_KEYS = [
  'size',
  'optional',
  'name',
  'font_family',
  'font_color',
  'font_bold',
  'font_italic',
  'mention',
  'question',
  'max_length',
  'instruction',
  'default_value',
  'read_only',
  'text',
  'name_format',
] as const satisfies readonly (keyof SignatureDraftField)[];

/** True when an in-place update would silently drop part of what the wizard holds. */
function needsRecreateToPersistDetails(
  field: SignatureDraftField,
  persisted: SignatureFieldInDb
): boolean {
  if ((field.reason ?? null) !== (persisted.reason ?? null)) return true;
  if ((field.vault_file_revision_uid ?? null) !== (persisted.vault_file_revision_uid ?? null)) {
    return true;
  }
  return LOCAL_ONLY_FIELD_DETAIL_KEYS.some(key => field[key] !== undefined && field[key] !== null);
}

/** A field as saved, minus the server uid the save itself hands back — what "changed" compares. */
export function fieldSaveKey(field: SignatureDraftField): string {
  return JSON.stringify({ ...field, persisted_uid: undefined });
}

/**
 * Reconcile the request's signature fields with what the wizard holds, and return the
 * server uid of every field the wizard keeps (keyed by its local uid), so the next save
 * can address them instead of recreating.
 *
 * A field unchanged since the last save (`previousFields`) and still on the server is left
 * alone. Changed ones are updated in place where possible: `SignatureFieldUpdate` covers
 * geometry, type and participant, so the per-type properties `SignatureFieldInDb` doesn't
 * expose survive untouched. A field whose unupdatable details the user did change is
 * recreated instead, and server fields the wizard no longer holds are deleted.
 */
async function syncSignatureFields(
  signingRequestUid: string,
  fields: SignatureDraftField[],
  participants: ParticipantInDb[],
  previousFields: SignatureDraftField[]
): Promise<Map<string, string>> {
  const existing = await fetchPersistedSignatureFields(signingRequestUid, participants);
  const existingByUid = new Map(existing.map(field => [field.uid, field]));
  const participantUidByEntityUid = buildParticipantUidByEntityUid(participants);
  const previousKeyByUid = new Map(previousFields.map(field => [field.uid, fieldSaveKey(field)]));

  const persistedUidByLocalUid = new Map<string, string>();
  const claimedUids = new Set<string>();
  for (const field of fields) {
    if (!field.recipient_id) {
      continue;
    }

    const participantUid = participantUidByEntityUid.get(field.recipient_id);
    if (!participantUid) {
      throw new Error(`Could not resolve participant for recipient ${field.recipient_id}.`);
    }

    // A participant or document removed above takes its fields with it, so only a field
    // still present on the server can be updated — the rest are recreated.
    const persisted = field.persisted_uid ? existingByUid.get(field.persisted_uid) : undefined;
    if (persisted && previousKeyByUid.get(field.uid) === fieldSaveKey(field)) {
      claimedUids.add(persisted.uid);
      persistedUidByLocalUid.set(field.uid, persisted.uid);
      continue;
    }
    if (!persisted || needsRecreateToPersistDetails(field, persisted)) {
      const createdUid = await createPersistedSignatureField(participantUid, signingRequestUid, {
        ...buildSignatureFieldCreatePayload(field),
      });
      persistedUidByLocalUid.set(field.uid, createdUid);
      continue;
    }

    claimedUids.add(persisted.uid);
    persistedUidByLocalUid.set(field.uid, persisted.uid);
    const update = await updateField({
      path: {
        signing_request_uid: signingRequestUid,
        // The path addresses the field where it lives today; the body moves it.
        participant_uid: persisted.participant_uid,
        signature_field_uid: persisted.uid,
      },
      body: {
        page: field.page,
        x: field.x,
        y: field.y,
        width: field.width ?? null,
        height: field.height ?? null,
        type: field.type,
        participant_uid: participantUid,
        ...(field.font_size === undefined ? {} : { font_size: field.font_size }),
      },
    });

    if (update.error) {
      throw new Error(extractErrorDetail(update.error, 'Failed to update signature field.'));
    }
  }

  for (const field of existing) {
    if (claimedUids.has(field.uid)) continue;

    const deletion = await deleteField({
      path: {
        signing_request_uid: signingRequestUid,
        participant_uid: field.participant_uid,
        signature_field_uid: field.uid,
      },
    });

    if (deletion.error) {
      throw new Error(extractErrorDetail(deletion.error, 'Failed to delete signature field.'));
    }
  }

  return persistedUidByLocalUid;
}

/** Reconcile the request's documents with the wizard's selection, keyed by revision uid. */
async function syncSigningRequestDocuments(
  signingRequestUid: string,
  documents: SignatureActivateRequest['documents']
): Promise<void> {
  const response = await getDocuments({ path: { signing_request_uid: signingRequestUid } });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, "Failed to load the signature request's documents.")
    );
  }

  const desiredByRevisionUid = new Map(
    documents.map(document => [document.vault_file_revision.uid, document])
  );

  const keptRevisionUids = new Set<string>();
  for (const existing of response.data) {
    // The revision the request is actually linked to — not `vault_file.current_revision`, which
    // is whatever is newest on the file today and would let a stale link pass as up to date.
    const revisionUid = existing.vault_file_revision.uid;

    const desired = desiredByRevisionUid.get(revisionUid);
    // There is no document update endpoint, so a changed signing file type is a re-add.
    if (desired && desired.signing_file_type === existing.signing_file_type) {
      keptRevisionUids.add(revisionUid);
      continue;
    }

    const deletion = await deleteDocument({
      path: { signing_request_uid: signingRequestUid, vault_file_revision_uid: revisionUid },
    });

    if (deletion.error) {
      throw new Error(
        extractErrorDetail(
          deletion.error,
          'Failed to remove a document from the signature request.'
        )
      );
    }
  }

  const toAdd = documents.filter(
    document => !keptRevisionUids.has(document.vault_file_revision.uid)
  );

  if (toAdd.length > 0) {
    await uploadYoutrustDocument(signingRequestUid, toAdd);
  }
}

/**
 * Reconcile the request's participants with the wizard's recipients, order included.
 *
 * The backend keeps no position: the signing order is creation order within each role.
 * So the recipients that already match, in place, from the top are kept, and everything
 * from the first difference on is removed and re-added in the wizard's order. There is
 * no participant update endpoint either, so a changed role, signature type or
 * representative is the same re-add.
 */
async function syncSigningRequestParticipants(
  signingRequestUid: string,
  participants: SignatureActivateRequest['participants']
): Promise<void> {
  const existing = await fetchPersistedParticipants(signingRequestUid);
  const desired = participants.participants;

  let kept = 0;
  while (kept < existing.length && kept < desired.length) {
    const current = existing[kept];
    const wanted = desired[kept];
    const same =
      current.contractual_party?.uid === wanted.contractual_party_uid &&
      participantSignature(wanted.role, wanted.signature_type, wanted.representative_uid) ===
        participantSignature(current.role, current.signature_type, current.representative?.uid);
    if (!same) break;
    kept += 1;
  }

  for (const current of existing.slice(kept)) {
    const deletion = await deleteParticipant({
      path: { signing_request_uid: signingRequestUid, participant_uid: current.uid },
    });

    if (deletion.error) {
      throw new Error(
        extractErrorDetail(
          deletion.error,
          'Failed to remove a participant from the signature request.'
        )
      );
    }
  }

  const toAdd = desired.slice(kept);
  if (toAdd.length > 0) {
    await addYoutrustSignees(signingRequestUid, { participants: toAdd });
  }
}

/** Open a new draft signing request — the first save of the wizard's autosave. */
export function createSignatureDraft(payload: SigningRequestCreate): Promise<SigningRequestInDb> {
  return initializeYoutrustSignature(payload);
}

const sameJson = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

/**
 * A document as the server links it: its revision and its role. The revision object
 * also carries live data (signature detection, previews) that changes without any edit.
 */
function documentsSaveKey(documents: SignatureActivateRequest['documents']): string {
  return JSON.stringify(
    documents.map(document => [document.vault_file_revision.uid, document.signing_file_type])
  );
}

/** Everything a save would send, as one comparable string — "is anything unsaved?". */
export function draftSaveKey(payload: SignatureActivateRequest): string {
  return JSON.stringify([
    payload.initialize,
    documentsSaveKey(payload.documents),
    payload.participants,
    payload.fields.map(fieldSaveKey),
  ]);
}

/**
 * Bring a draft signing request up to the wizard's state, sending only the parts that
 * differ from `previous` (what the last save left on the server; null = nothing known,
 * sync it all). Returns the server uid of every kept field by its local uid.
 *
 * Order matters: documents and participants first, since removing either takes its
 * fields with it; fields last, re-read against the participants that now exist.
 */
export async function saveSignatureDraft(
  signingRequestUid: string,
  payload: SignatureActivateRequest,
  previous: SignatureActivateRequest | null
): Promise<Map<string, string>> {
  if (!previous || !sameJson(payload.initialize, previous.initialize)) {
    const response = await updateSigningRequest({
      path: { signing_request_uid: signingRequestUid },
      body: {
        name: payload.initialize.name,
        message: payload.initialize.message ?? null,
        delivery_mode: payload.initialize.delivery_mode ?? null,
        ordered_approvers: payload.initialize.ordered_approvers ?? null,
        ordered_signers: payload.initialize.ordered_signers ?? null,
        expiration_date: payload.initialize.expiration_date ?? null,
      },
    });

    if (response.error) {
      throw new Error(
        extractErrorDetail(response.error, 'Failed to update the signature request.')
      );
    }
  }

  const documentsChanged =
    !previous || documentsSaveKey(payload.documents) !== documentsSaveKey(previous.documents);
  const participantsChanged = !previous || !sameJson(payload.participants, previous.participants);
  if (documentsChanged) await syncSigningRequestDocuments(signingRequestUid, payload.documents);
  if (participantsChanged) {
    await syncSigningRequestParticipants(signingRequestUid, payload.participants);
  }

  const fieldsChanged =
    !previous || !sameJson(payload.fields.map(fieldSaveKey), previous.fields.map(fieldSaveKey));
  if (!documentsChanged && !participantsChanged && !fieldsChanged) {
    return new Map(
      payload.fields.flatMap(field =>
        field.persisted_uid ? [[field.uid, field.persisted_uid] as const] : []
      )
    );
  }

  // Re-fetch after the churn above: fields address recipients by entity uid, and both the
  // participant uids and the surviving field set may have just changed.
  const persistedParticipants = await fetchPersistedParticipants(signingRequestUid);
  return syncSignatureFields(
    signingRequestUid,
    payload.fields,
    persistedParticipants,
    previous?.fields ?? []
  );
}

/** Send a saved draft out for signing. The draft must already hold everything (save first). */
export async function activateSignatureDraft(
  signingRequestUid: string
): Promise<SignatureActivateResponse> {
  await activateYoutrustSignature(signingRequestUid);
  return { signing_request_uid: signingRequestUid };
}
